import { useEffect, useMemo } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { Divider, Skeleton, Stack, Tab, Tabs } from '@mui/material'
import { useGetRollupConfigs } from '@/service-library/hooks/rollup-configs'
import { useGetVendorExclusions } from '@/service-library/hooks/vendor-exclusions'
import { useOrganizationsContext } from '@/components/organizations/OrganizationsProvider'
import PageTitle from '@/components/PageTitle'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import ConfigsTab from './ConfigsTab'
import useLineItemRollupAvailable from './useLineItemRollupAvailable'
import VendorExclusionsTab from './VendorExclusionsTab'

// The rollup_configs flag is enforced where the app enforces all flag-gated
// pages: this page's route registration in Application.tsx and its nav item
// in ProjectSettingsLayout. By the time this renders, the flag is on.
export default function LineItemRollupPage() {
  const { project } = useProjectContext()
  const navigate = useNavigate()
  const { search } = useLocation()
  // Tab selection is driven by the route's splat segment (same pattern as
  // WorkflowStatesPage) — anything other than 'exclusions', including the
  // bare page URL, shows Configs.
  const { '*': splat } = useParams()
  const activeTab = splat === 'exclusions' ? 'exclusions' : 'configs'

  // Rollup requires eligible key info types on the integration and a line
  // items table on the project — same redirect pattern as
  // PurchaseOrderMatchingPage when its project feature is off.
  const { isAvailable: isRollupAvailable, isPending: isPendingAvailability } =
    useLineItemRollupAvailable()

  useEffect(() => {
    if (!isPendingAvailability && !isRollupAvailable) {
      navigate(`/projects/${project.id}/settings/general`, { replace: true })
    }
  }, [isPendingAvailability, isRollupAvailable, navigate, project.id])

  const {
    rollupConfigs,
    queryKey: rollupConfigsQueryKey,
    isPending: isPendingConfigs,
  } = useGetRollupConfigs({
    filters: {
      project_id: project.id,
      limit: '1000',
    },
  })

  const {
    vendorExclusions,
    queryKey: vendorExclusionsQueryKey,
    isPending: isPendingExclusions,
  } = useGetVendorExclusions({
    filters: {
      project_id: project.id,
      limit: '1000',
    },
  })

  const { organizations, isFetchingAll: isLoadingOrgs } =
    useOrganizationsContext()

  const dataProviderOrgs = useMemo(
    () => organizations.filter((org) => org.is_relay_data_provider),
    [organizations],
  )

  const isPending =
    isPendingConfigs ||
    isPendingExclusions ||
    isLoadingOrgs ||
    isPendingAvailability

  return (
    <>
      <PageTitle>Line Item Rollup</PageTitle>
      <ProjectBreadcrumb
        label="Line Item Rollup"
        url={`/projects/${project.id}/settings/line-item-rollup`}
      />

      {/* Each tab owns its primary action and dialogs, so the action sits in
          the tab's section heading rather than in the page heading. */}
      <SettingsPage
        title="Line Item Rollup"
        description="Control how line items are merged before they're exported to your ERP."
        width="lg"
      >
        {isPending ? (
          <Stack spacing={3}>
            <Stack direction="row" spacing={2}>
              <Skeleton width={80} />
              <Skeleton width={160} />
            </Stack>
            <Skeleton variant="rounded" height={56} />
          </Stack>
        ) : (
          <Stack spacing={3}>
            {/* Each tab is a link, so the route stays the source of truth
                for which one is open. The negative margin takes back the
                12px of padding above each tab's label, so the gap under the
                page heading looks the same as the gap between sections. */}
            <div>
              <Tabs value={activeTab} sx={{ mt: -1.5 }}>
                <Tab
                  label="Configs"
                  value="configs"
                  component={Link}
                  to={`/projects/${project.id}/settings/line-item-rollup/configs${search}`}
                />
                <Tab
                  label={`Vendor exclusions (${vendorExclusions.length})`}
                  value="exclusions"
                  component={Link}
                  to={`/projects/${project.id}/settings/line-item-rollup/exclusions${search}`}
                />
              </Tabs>
              <Divider />
            </div>

            {activeTab === 'configs' ? (
              <ConfigsTab
                project={project}
                dataProviderOrgs={dataProviderOrgs}
                vendorExclusions={vendorExclusions}
                rollupConfigs={rollupConfigs}
                rollupConfigsQueryKey={rollupConfigsQueryKey}
                isPending={isPendingConfigs}
              />
            ) : (
              <VendorExclusionsTab
                project={project}
                dataProviderOrgs={dataProviderOrgs}
                vendorExclusions={vendorExclusions}
                vendorExclusionsQueryKey={vendorExclusionsQueryKey}
                isPending={isPendingExclusions}
              />
            )}
          </Stack>
        )}
      </SettingsPage>
    </>
  )
}
